import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Calendar, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { useOssAuth } from "../../context/OssAuthContext";
import OssSectionPageHeader from "../../components/oss/OssSectionPageHeader";
import OssTurniGestione from "./OssTurniGestione";
import OssTurniLegend from "../../components/oss/OssTurniLegend";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_TURNI_CONFIG = {
  schema_orario: "standard",
  max_notti_consecutive: 3,
  riposo_dopo_notti: 1,
  orari: {
    mattina: { inizio: "07:00", fine: "14:00" },
    pomeriggio: { inizio: "14:00", fine: "21:00" },
    notte: { inizio: "21:00", fine: "07:00" },
    lunga: { inizio: "07:00", fine: "21:00" },
  },
  pomeriggio_corto: { enabled: false, ora_inizio: "14:00", ora_fine: "20:00" },
};

function mergeTurniConfig(config) {
  return {
    ...DEFAULT_TURNI_CONFIG,
    ...(config || {}),
    orari: {
      ...DEFAULT_TURNI_CONFIG.orari,
      ...(config?.orari || {}),
    },
    pomeriggio_corto: {
      ...DEFAULT_TURNI_CONFIG.pomeriggio_corto,
      ...(config?.pomeriggio_corto || {}),
    },
  };
}

function formatTurnoRange(range) {
  if (!range?.inizio || !range?.fine) return "";
  return `${range.inizio.replace(":00", "")}-${range.fine.replace(":00", "")}`;
}

function getMonthRange(firstDay) {
  const from = firstDay;
  const d = new Date(firstDay + "T12:00:00");
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return { from, to: d.toISOString().slice(0, 10) };
}

function formatDateLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function getDayLetterIt(date) {
  const letters = ["D", "L", "M", "M", "G", "V", "S"];
  return letters[date.getDay()];
}

function addDaysToDateStr(dateStr, days) {
  const d = new Date(`${dateStr}T12:00:00`);
  d.setDate(d.getDate() + days);
  return formatDateLocal(d);
}

function useShiftDisplay(turni, ferie, turniConfig) {
  return useMemo(() => {
    const config = mergeTurniConfig(turniConfig);
    const turnoOrari = config.orari || DEFAULT_TURNI_CONFIG.orari;

    const getFerieForDate = (dateStr) =>
      ferie.find((f) => f.from_date <= dateStr && f.to_date >= dateStr) || null;

    const getRiposoInfoForDate = (dateStr) => {
      const maxNotti = Math.max(1, parseInt(config.max_notti_consecutive, 10) || 1);
      const riposoDays = Math.max(0, parseInt(config.riposo_dopo_notti, 10) || 0);
      if (riposoDays <= 0) return null;

      const nightDates = turni
        .filter((t) => t.turno === "notte")
        .map((t) => t.data)
        .filter(Boolean)
        .sort();

      let consecutive = 0;
      let previous = null;
      const restByDate = {};

      nightDates.forEach((nightDate) => {
        consecutive = previous && nightDate === addDaysToDateStr(previous, 1) ? consecutive + 1 : 1;
        if (consecutive >= maxNotti) {
          for (let i = 1; i <= riposoDays; i++) {
            const restDate = addDaysToDateStr(nightDate, i);
            restByDate[restDate] =
              i === 1
                ? {
                    code: "SN",
                    type: "smonto_notte",
                    bg: "bg-indigo-200",
                    text: "text-indigo-900",
                    label: "Smonto notte",
                  }
                : {
                    code: "R",
                    type: "riposo_notte",
                    bg: "bg-violet-200",
                    text: "text-violet-900",
                    label: "Riposo dopo notti",
                  };
          }
          consecutive = 0;
        }
        previous = nightDate;
      });

      return restByDate[dateStr] || null;
    };

    const labelTurnoConOrario = (label, range) => {
      const formatted = formatTurnoRange(range);
      return formatted ? `${label} (${formatted})` : label;
    };

    const getTurnoRangeFromRecord = (turnoRecord, fallbackKey) => {
      if (turnoRecord?.ora_inizio && turnoRecord?.ora_fine) {
        return { inizio: turnoRecord.ora_inizio, fine: turnoRecord.ora_fine };
      }
      return turnoOrari[fallbackKey];
    };

    const isPomeriggioCorto = (turnoRecord) => {
      if (!turnoRecord || turnoRecord.turno !== "pomeriggio") return false;
      if (turnoRecord.variante === "pomeriggio_corto") return true;
      return Boolean(
        turnoRecord.ora_inizio &&
          turnoRecord.ora_fine &&
          (turnoRecord.ora_inizio !== turnoOrari.pomeriggio?.inizio ||
            turnoRecord.ora_fine !== turnoOrari.pomeriggio?.fine)
      );
    };

    const getShiftInfoForDate = (dateStr) => {
      if (getFerieForDate(dateStr)) {
        return { code: "F", type: "ferie", bg: "bg-amber-200", text: "text-amber-900", label: "Ferie" };
      }
      const dayTurni = turni.filter((t) => t.data === dateStr);
      if (dayTurni.length === 0) return getRiposoInfoForDate(dateStr);
      const hasSmontante = dayTurni.some((t) => t.tipo_giorno === "smontante" || t.turno === "smontante");
      const hasRiposo = dayTurni.some((t) => t.tipo_giorno === "riposo" || t.turno === "riposo");
      if (hasSmontante) {
        return { code: "S", type: "smontante", bg: "bg-indigo-200", text: "text-indigo-900", label: "Smontante" };
      }
      if (hasRiposo) {
        return { code: "R", type: "riposo", bg: "bg-violet-200", text: "text-violet-900", label: "Riposo" };
      }
      const hasMattina = dayTurni.some((t) => t.turno === "mattina");
      const hasPomeriggio = dayTurni.some((t) => t.turno === "pomeriggio");
      const hasNotte = dayTurni.some((t) => t.turno === "notte");
      const mattinaRecord = dayTurni.find((t) => t.turno === "mattina");
      const pomeriggioRecord = dayTurni.find((t) => t.turno === "pomeriggio");
      const notteRecord = dayTurni.find((t) => t.turno === "notte");
      if (hasMattina && hasPomeriggio) {
        return {
          code: "M-P",
          type: "lunga",
          bg: "bg-emerald-300",
          text: "text-emerald-900",
          label: labelTurnoConOrario("Lunga", turnoOrari.lunga),
        };
      }
      if (hasMattina) {
        return {
          code: "M",
          type: "mattina",
          bg: "bg-sky-200",
          text: "text-sky-900",
          label: labelTurnoConOrario("Mattina", getTurnoRangeFromRecord(mattinaRecord, "mattina")),
        };
      }
      if (hasPomeriggio) {
        const corto = isPomeriggioCorto(pomeriggioRecord);
        return {
          code: corto ? "P*" : "P",
          type: "pomeriggio",
          bg: corto ? "bg-fuchsia-200" : "bg-rose-200",
          text: corto ? "text-fuchsia-900" : "text-rose-900",
          label: labelTurnoConOrario(corto ? "Pomeriggio corto" : "Pomeriggio", getTurnoRangeFromRecord(pomeriggioRecord, "pomeriggio")),
        };
      }
      if (hasNotte) {
        return {
          code: "N",
          type: "notte",
          bg: "bg-slate-300",
          text: "text-slate-800",
          label: labelTurnoConOrario("Notte", getTurnoRangeFromRecord(notteRecord, "notte")),
        };
      }
      return null;
    };

    return { getShiftInfoForDate };
  }, [turni, ferie, turniConfig]);
}

function OssTurniPersonali() {
  const { token, activeOss: oss } = useOssAuth();
  const [turni, setTurni] = useState([]);
  const [ferie, setFerie] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [turniConfig, setTurniConfig] = useState(DEFAULT_TURNI_CONFIG);
  const [loadingTurni, setLoadingTurni] = useState(false);
  const [monthStart, setMonthStart] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return `${d.toISOString().slice(0, 7)}-01`;
  });

  const { getShiftInfoForDate } = useShiftDisplay(turni, ferie, turniConfig);
  const turnoOrari = turniConfig.orari || DEFAULT_TURNI_CONFIG.orari;
  const pomeriggioCortoConfig = turniConfig.pomeriggio_corto || DEFAULT_TURNI_CONFIG.pomeriggio_corto;

  useEffect(() => {
    if (!token || !monthStart) return;
    const { from, to } = getMonthRange(monthStart);
    setLoadingTurni(true);
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API_URL}/api/oss/me/turni`, {
        params: { from_date: from, to_date: to },
        headers,
      }),
      axios.get(`${API_URL}/api/oss/me/ferie`, {
        params: { from_date: from, to_date: to },
        headers,
      }),
      axios.get(`${API_URL}/api/oss/me/turni-config`, {
        headers,
      }),
    ])
      .then(([turniRes, ferieRes, configRes]) => {
        setTurni(turniRes.data.turni || []);
        setFerie(ferieRes.data.ferie || []);
        setTurniConfig(mergeTurniConfig(configRes.data.config));
      })
      .catch(() => {
        setTurni([]);
        setFerie([]);
        setTurniConfig(DEFAULT_TURNI_CONFIG);
      })
      .finally(() => setLoadingTurni(false));
  }, [token, monthStart]);

  const prevMonth = () => {
    const d = new Date(`${monthStart}T12:00:00`);
    d.setMonth(d.getMonth() - 1);
    setMonthStart(`${d.toISOString().slice(0, 7)}-01`);
  };

  const nextMonth = () => {
    const d = new Date(`${monthStart}T12:00:00`);
    d.setMonth(d.getMonth() + 1);
    setMonthStart(`${d.toISOString().slice(0, 7)}-01`);
  };

  const first = new Date(`${monthStart}T12:00:00`);
  const year = first.getFullYear();
  const month = first.getMonth();
  const lastDayOfMonth = new Date(year, month + 1, 0).getDate();

  const daysInMonth = [];
  for (let i = 1; i <= lastDayOfMonth; i++) {
    const d = new Date(year, month, i);
    daysInMonth.push({
      dateStr: formatDateLocal(d),
      dayNum: i,
      dayLetter: getDayLetterIt(d),
      isSunday: d.getDay() === 0,
      isSaturday: d.getDay() === 6,
    });
  }

  const ossDisplayName =
    [oss?.cognome, oss?.nome].filter(Boolean).join(" ").trim() || "Tu";

  return (
    <div>
      <OssSectionPageHeader
        sectionKey="turni"
        title="I miei turni"
        subtitle={
          oss?.nome || oss?.cognome
            ? `Benvenuto, ${[oss?.nome, oss?.cognome].filter(Boolean).join(" ")}`
            : "Calendario turni e presenze"
        }
      />

      <Card className="card-base oss-surface bg-white">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2 text-sky-700">
              <Calendar className="w-5 h-5 shrink-0" />
              Calendario turni
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="icon"
                className="h-11 w-11 touch-manipulation"
                onClick={prevMonth}
                aria-label="Mese precedente"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm font-medium text-slate-700 min-w-[160px] text-center uppercase">
                {first.toLocaleDateString("it-IT", { month: "long", year: "numeric" })}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="h-11 w-11 touch-manipulation"
                onClick={nextMonth}
                aria-label="Mese successivo"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap text-xs text-slate-600 mt-1">
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-4 h-4 rounded-sm bg-sky-200 border border-sky-300 text-center leading-4 font-bold text-sky-900">
                M
              </span>
              Mattina {formatTurnoRange(turnoOrari.mattina)}
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-4 h-4 rounded-sm bg-rose-200 border border-rose-300 text-center leading-4 font-bold text-rose-900">
                P
              </span>
              Pomeriggio {formatTurnoRange(turnoOrari.pomeriggio)}
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-4 h-4 rounded-sm bg-slate-300 border border-slate-400 text-center leading-4 font-bold text-slate-800">
                N
              </span>
              Notte {formatTurnoRange(turnoOrari.notte)}
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-auto px-1 h-4 rounded-sm bg-emerald-300 border border-emerald-400 text-center leading-4 font-bold text-emerald-900 text-[10px]">
                M-P
              </span>
              Lunga {formatTurnoRange(turnoOrari.lunga)}
            </span>
            {pomeriggioCortoConfig.enabled && (
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-auto px-1 h-4 rounded-sm bg-fuchsia-200 border border-fuchsia-300 text-center leading-4 font-bold text-fuchsia-900 text-[10px]">
                  P*
                </span>
                Pomeriggio corto {pomeriggioCortoConfig.ora_inizio?.replace(":00", "")}-
                {pomeriggioCortoConfig.ora_fine?.replace(":00", "")}
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-4 h-4 rounded-sm bg-indigo-200 border border-indigo-300 text-center leading-4 font-bold text-indigo-900">
                S
              </span>
              Smontante
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-auto px-1 h-4 rounded-sm bg-indigo-200 border border-indigo-300 text-center leading-4 font-bold text-indigo-900 text-[10px]">
                SN
              </span>
              Smonto notte
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-4 h-4 rounded-sm bg-violet-200 border border-violet-300 text-center leading-4 font-bold text-violet-900">
                R
              </span>
              Riposo dopo notti
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-4 h-4 rounded-sm bg-amber-200 border border-amber-300 text-center leading-4 font-bold text-amber-900">
                F
              </span>
              Ferie
            </span>
            <span className="text-slate-400">
              · {turniConfig.schema_orario === "sei_ore"
                ? "Schema 6 ore: S/R salvati dal ciclo NNSRLP."
                : `Smonto/riposo indicativi dopo ${turniConfig.max_notti_consecutive} notti per ${
                    turniConfig.riposo_dopo_notti === 1 ? "1 giorno" : `${turniConfig.riposo_dopo_notti} giorni`
                  }.`}
            </span>
          </div>
        </CardHeader>
        <CardContent className="p-3 sm:p-4">
          {loadingTurni ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-sky-500" aria-hidden />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto -mx-1">
                <table className="border-collapse text-xs" style={{ tableLayout: "fixed" }}>
                  <thead>
                    <tr>
                      <th
                        rowSpan={2}
                        className="sticky left-0 z-20 bg-slate-100 border border-slate-300 px-2 py-1 text-left font-semibold text-slate-700 align-middle"
                        style={{ width: 140, minWidth: 140 }}
                      >
                        OSS
                      </th>
                      {daysInMonth.map((d) => (
                        <th
                          key={`dl-${d.dateStr}`}
                          className={`border border-slate-300 px-0 py-1 text-center font-semibold ${
                            d.isSunday
                              ? "bg-rose-100 text-rose-700"
                              : d.isSaturday
                                ? "bg-slate-50 text-slate-700"
                                : "bg-slate-100 text-slate-700"
                          }`}
                          style={{ width: 44, minWidth: 44 }}
                        >
                          {d.dayLetter}
                        </th>
                      ))}
                    </tr>
                    <tr>
                      {daysInMonth.map((d) => (
                        <th
                          key={`dn-${d.dateStr}`}
                          className={`border border-slate-300 px-0 py-1 text-center font-semibold ${
                            d.isSunday
                              ? "bg-rose-100 text-rose-700"
                              : d.isSaturday
                                ? "bg-slate-50 text-slate-700"
                                : "bg-slate-100 text-slate-700"
                          }`}
                          style={{ width: 44, minWidth: 44 }}
                        >
                          {d.dayNum}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td
                        className="sticky left-0 z-10 border border-slate-300 px-2 py-1 font-semibold text-slate-800 bg-white"
                        style={{ width: 140, minWidth: 140 }}
                      >
                        <div className="truncate uppercase text-[11px] leading-tight">{oss?.cognome || "—"}</div>
                        <div className="truncate text-[10px] font-normal text-slate-500 leading-tight">
                          {oss?.nome || ""}
                        </div>
                      </td>
                      {daysInMonth.map((d) => {
                        const info = getShiftInfoForDate(d.dateStr);
                        const baseBg = d.isSunday ? "bg-rose-50" : d.isSaturday ? "bg-slate-50" : "bg-white";
                        const cellBg = info ? info.bg : baseBg;
                        const cellText = info ? info.text : "text-slate-400";
                        const label = info?.label ?? "nessun turno";
                        const selected = selectedDay === d.dateStr;
                        return (
                          <td
                            key={d.dateStr}
                            className={`border border-slate-300 p-0 text-center ${cellBg} ${cellText}`}
                            style={{ width: 44, minWidth: 44, height: 44 }}
                          >
                            <button
                              type="button"
                              onClick={() => setSelectedDay(selected ? null : d.dateStr)}
                              aria-pressed={selected}
                              aria-label={`${d.dayNum}: ${label}`}
                              className={`flex h-11 w-11 items-center justify-center touch-manipulation focus-visible:outline-none ${
                                selected ? "ring-2 ring-inset ring-sky-600" : ""
                              }`}
                            >
                              {info ? (
                                <span className={`font-bold ${info.code.length > 1 ? "text-[10px]" : "text-xs"}`}>{info.code}</span>
                              ) : null}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
              {selectedDay ? (
                <div role="status" data-testid="oss-turno-dettaglio" className="mt-3 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
                  <span className="font-semibold">
                    {new Date(`${selectedDay}T12:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
                  </span>
                  {": "}
                  {getShiftInfoForDate(selectedDay)?.label ?? "nessun turno"}
                </div>
              ) : null}
              <OssTurniLegend personal className="mt-4" />
              {turni.length === 0 && ferie.length === 0 && (
                <p className="text-sm text-slate-500 mt-3 text-center">
                  Nessun turno o ferie in questo mese. Usa le frecce per cambiare mese.
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function OssTurni() {
  const { activeOss } = useOssAuth();
  if (activeOss?.responsabile) {
    return <OssTurniGestione />;
  }
  return <OssTurniPersonali />;
}
